/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useMutation, useReadQuery } from '@apollo/client/react'
import { ErrorAlert } from '@repo/shared-client/components/blocks'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import VmForm from '@web/src/features/vendor-market/vm-form/vm-form'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { useState } from 'react'
import toast from 'react-hot-toast'
import { VendorMarketInfo } from 'src/lib/fragments'
import type {
  EditVendorMarketQuery,
  EditVendorMarketQueryVariables,
  UpdateVendorMarketInput,
  UpdateVendorMarketMutation,
  UpdateVendorMarketMutationVariables,
} from 'types/graphql'

const EDIT_VM_QUERY: TypedDocumentNode<EditVendorMarketQuery, EditVendorMarketQueryVariables> = gql`
  query EditVendorMarket($vendorMarketIdOrSlug: String!) {
    vendorMarket(vendorMarketIdOrSlug: $vendorMarketIdOrSlug) {
      ...VendorMarketInfo
    }
  }
  ${VendorMarketInfo}
`

const UPDATE_VM_MUTATION: TypedDocumentNode<UpdateVendorMarketMutation, UpdateVendorMarketMutationVariables> = gql`
  mutation UpdateVendorMarket($vendorMarketIdOrSlug: String!, $input: UpdateVendorMarketInput!) {
    updateVendorMarket(vendorMarketIdOrSlug: $vendorMarketIdOrSlug, input: $input) {
      ...VendorMarketInfo
    }
  }
  ${VendorMarketInfo}
`

export const Route = createFileRoute('/(auth)/(main-layout)/vendor-market/$vendorMarketIdOrSlug_/edit')({
  component: EditVendorMarketPage,
  head: () =>
    seo({
      title: `Edit Vendor | ${env.VITE_APP_NAME}`,
      description: 'Edit a vendor listing',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
      meta: [{ name: 'rating', content: 'adult' }],
    }),
  loader: async ({ context, params }) => {
    const { queryRef } = await queryAndPreload(context, EDIT_VM_QUERY, {
      variables: { vendorMarketIdOrSlug: params.vendorMarketIdOrSlug },
    })
    return { queryRef }
  },
})

function EditVendorMarketPage() {
  const { t } = useTranslation()
  const { vendorMarketIdOrSlug } = Route.useParams()
  const [showSpinner, setShowSpinnerValue] = useState(false)
  const navigate = useNavigate()
  const { queryRef } = Route.useLoaderData()
  const { data, error, networkStatus } = useReadQuery(queryRef as QueryRef<EditVendorMarketQuery>)
  const isInitialLoading = networkStatus === NetworkStatus.loading

  const [updateVM] = useMutation(UPDATE_VM_MUTATION, {
    onCompleted: (result) => {
      toast.success(t('Vendor Updated'))
      navigate({
        to: '/vendor-market/$vendorMarketIdOrSlug',
        params: {
          vendorMarketIdOrSlug: result.updateVendorMarket.slug ?? result.updateVendorMarket.id,
        },
      } as never)
      setShowSpinnerValue(false)
    },
    onError: (err) => {
      toast.error(err.message)
      setShowSpinnerValue(false)
    },
  })

  const onSave = (
    input: UpdateVendorMarketInput,
    idParam?: NonNullable<EditVendorMarketQuery['vendorMarket']>['id']
  ) => {
    setShowSpinnerValue(true)
    updateVM({
      variables: { vendorMarketIdOrSlug: idParam ?? vendorMarketIdOrSlug, input },
    })
  }

  if (isInitialLoading) return <SFPageLoading />
  if (error) return <ErrorAlert error={error} />
  if (!data?.vendorMarket) return <ErrorAlert error={new Error(t('Vendor not found'))} />

  return (
    <div className="mx-auto w-full max-w-2xl pt-4">
      <div className="flex w-full flex-col items-stretch gap-4">
        <h1 className="w-full text-center font-bold text-4xl">
          {t('Edit')} {t('Vendor')}
        </h1>
        <VmForm onSave={onSave} showSpinner={showSpinner} vendorMarket={data.vendorMarket} />
      </div>
    </div>
  )
}
